
import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  CheckCircle2,
  HeartHandshake,
} from "lucide-react";

export default function WelcomeSection() {
  return (
    <section className="relative isolate w-full overflow-hidden bg-[#FFFCF8] py-10 sm:py-12 lg:py-14">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -left-32 top-10 h-80 w-80 rounded-full bg-red-100/50 blur-3xl" />
        <div
          className="absolute inset-0 opacity-[0.025]"
          style={{
            backgroundImage:
              "radial-gradient(#991b1b 1px, transparent 1px)",
            backgroundSize: "26px 26px",
          }}
        />
      </div>

      <div className="relative z-10 mx-auto w-full max-w-[1600px] px-5 sm:px-8 lg:px-12 xl:px-6">

        {/* Main Heading - Single Line on Desktop */}
        <h1 className="mb-10 text-center font-serif text-[28px] font-black leading-tight tracking-tight text-slate-950 sm:text-[37px] lg:whitespace-nowrap lg:text-[42px] xl:text-[51px]">
          Welcome to{" "}
          <span className="text-red-800">
            St. Xavier&apos;s
          </span>{" "}
          Public School
        </h1>

        {/* Main Content */}
        <div className="grid items-center gap-10 lg:grid-cols-[1.08fr_0.92fr] lg:gap-14">

          {/* LEFT CONTENT */}
          <div>

            <div className="mb-5 flex items-center gap-3">
              <span className="h-[3px] w-9 rounded-full bg-amber-500" />

              <p className="text-xs font-black uppercase tracking-[0.18em] text-amber-700">
                The Pride of Purvanchal
              </p>
            </div>

            {/* Tagline */}
            <div className="border-l-[3px] border-amber-500 pl-4">
              <p className="max-w-xl font-serif text-lg font-semibold italic leading-relaxed text-slate-700">
                "Inspiring young minds, nurturing strong values,
                and building brighter futures."
              </p>
            </div>

            {/* About Description */}
            <div className="mt-6 max-w-2xl space-y-4 text-[14px] leading-[1.9] text-slate-600 sm:text-[15px]">

              <p>
                <strong className="text-red-800">
                  St. Xavier&apos;s Public School
                </strong>
                , proudly recognized as the{" "}
                <strong className="text-slate-900">
                  Pride of Purvanchal
                </strong>
                , is an English Medium School dedicated to
                providing meaningful education rooted in
                knowledge, discipline and strong moral values.
              </p>

              <p>
                We believe that true education goes beyond
                textbooks and examinations. Our learning
                environment encourages students to think
                independently, explore their abilities,
                develop confidence and grow into responsible
                individuals.
              </p>

              <p>
                With a focus on academic excellence, character
                development and all-round growth, we strive to
                prepare every child for the opportunities and
                challenges of tomorrow.
              </p>
            </div>

            {/* Highlight Statement */}
            <div className="mt-6 flex items-start gap-3 rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 to-white px-4 py-4">

              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
                <HeartHandshake size={20} />
              </div>

              <div>
                <h3 className="text-sm font-extrabold text-slate-900">
                  Every Child Matters
                </h3>

                <p className="mt-1 text-xs leading-relaxed text-slate-600">
                  Equal opportunities, individual attention
                  and a supportive environment where every
                  learner can grow with confidence.
                </p>
              </div>
            </div>

            {/* CTA */}
            <div className="mt-7 flex flex-wrap items-center gap-4">
              <Link
                to="/about-us"
                className="group inline-flex items-center gap-3 rounded-full bg-red-800 px-7 py-3 text-sm font-bold text-white shadow-lg shadow-red-900/15 transition-all duration-300 hover:-translate-y-1 hover:bg-red-900"
              >
                Discover More About Us

                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white/20">
                  <ArrowUpRight
                    size={16}
                    className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  />
                </span>
              </Link>

              <div className="flex items-center gap-2 text-xs font-semibold text-slate-600">
                <CheckCircle2
                  size={17}
                  className="text-amber-600"
                />
                Learning • Values • Excellence
              </div>
            </div>

          </div>

          {/* RIGHT SIDE - CLEAN IMAGE */}
          <div className="relative mx-auto w-full max-w-[650px]">

            {/* Decorative Corner Accents */}
            <div className="pointer-events-none absolute -right-3 -top-3 h-32 w-32 rounded-[30px] border-[7px] border-amber-300/80 sm:-right-5 sm:-top-5" />

            <div className="pointer-events-none absolute -bottom-4 -left-4 h-36 w-36 rounded-[30px] bg-red-800 sm:-bottom-5 sm:-left-5" />

            {/* Image Frame */}
            <div className="relative z-10 rounded-[26px] bg-white p-2.5 shadow-[0_20px_55px_rgba(90,20,20,0.15)] sm:rounded-[32px] sm:p-3">

              <div className="group relative h-[340px] overflow-hidden rounded-[20px] bg-slate-100 sm:h-[430px] sm:rounded-[25px] lg:h-[490px] xl:h-[510px]">

                <img
                  src="/image1.png"
                  alt="St. Xavier's Public School"
                  loading="lazy"
                  className="h-full w-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-[1.035]"
                />

              </div>
            </div>

            {/* Decorative Dots */}
            <div className="pointer-events-none absolute -bottom-9 right-5 z-0 grid grid-cols-5 gap-2 opacity-40">
              {Array.from({ length: 20 }).map((_, index) => (
                <span
                  key={index}
                  className="h-1.5 w-1.5 rounded-full bg-red-700"
                />
              ))}
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}
